import React from "react";
import { useTheme } from "../../context/ThemeContext";
import "./footer-hero.css";

const Icon = ({ kind }) => {
  const common = { width: 18, height: 18, fill: "currentColor" };
  switch (kind) {
    case "facebook": return <svg {...common} viewBox="0 0 24 24"><path d="M13.5 9H16V6h-2.5C11.6 6 11 7.1 11 8.8V10H9v3h2v7h3v-7h2.2l.3-3H14v-1c0-.6.2-1 .8-1Z"/></svg>;
    case "x": return <svg {...common} viewBox="0 0 24 24"><path d="M18.3 4.5h2.1l-6.1 7 7 8.9h-5.5l-4.2-5.4-4.8 5.4H4.7l6.5-7.2L4.7 4.5h5.6l3.9 5.1 4.1-5.1Z"/></svg>;
    case "instagram": return <svg {...common} viewBox="0 0 24 24"><path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm5.75-3.25a1 1 0 1 1-1 1 1 1 0 0 1 1-1Z"/></svg>;
    case "linkedin": return <svg {...common} viewBox="0 0 24 24"><path d="M4.98 3.5a2.5 2.5 0 1 0 .02 5 2.5 2.5 0 0 0-.02-5ZM3 8.98h4v12H3v-12Zm7 0h3.83v1.64h.05c.53-1 1.82-2.06 3.75-2.06 4.01 0 4.75 2.64 4.75 6.07v6.35h-4v-5.64c0-1.35-.03-3.1-1.89-3.1-1.9 0-2.19 1.48-2.19 3v5.74h-4v-12Z"/></svg>;
    default: return null;
  }
};

export default function FooterHero({
  brand = { name: "EPC Spark", tagline: "Spark your EPC workflow", logoSrc: "", logoAlt: "EPC Spark" },
  useLinks = [
    { label: "Feature", href: "#features" },
    { label: "About", href: "#about" },
    { label: "Testimonial", href: "#testimonials" },
    { label: "Pricing", href: "#pricing" },
    { label: "Contact", href: "#contact" },
    { label: "Blog", href: "#blog" },
  ],
  companyLinks = [],
  addressLines = ["128 City Road, London, EC1V 2NX", "United Kingdom"],
  socials = [
    { kind: "facebook", href: "#", label: "Facebook" },
    { kind: "x", href: "#", label: "X" },
    { kind: "instagram", href: "#", label: "Instagram" },
    { kind: "linkedin", href: "#", label: "LinkedIn" },
  ],
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <footer aria-labelledby="site-footer-heading" className={`footer-hero border-top ${isDark ? 'footer-hero-dark' : 'footer-hero-light'}`} role="contentinfo">
      <div className="container py-5 py-lg-6">
        <div className="row g-4 align-items-start">
          {/* Left: brand + tagline + socials */}
          <div className="col-12 col-lg-6">
            <div className="d-flex align-items-center gap-3 mb-2">
              {brand.logoSrc ? (
                <img 
                  src={brand.logoSrc} 
                  alt={brand.logoAlt || brand.name} 
                  width={40} 
                  height={40} 
                  className="rounded-3"
                  style={{ objectFit: 'contain' }}
                />
              ) : (
                <div className="footer-logo-placeholder rounded-3" aria-hidden="true"></div>
              )}
              <h2 id="site-footer-heading" className={`h2 m-0 fw-semibold ${isDark ? 'text-white' : 'text-dark'}`}>
                {brand.name}
              </h2>
            </div>

            {brand.tagline && <p className={`mb-4 ${isDark ? 'text-white-50' : 'text-muted'}`}>{brand.tagline}</p>}

            <div className="d-flex gap-3">
              {socials.map((s, i) => (
                <a key={i} href={s.href} aria-label={s.label || s.kind}
                   className={`footer-social d-inline-flex align-items-center justify-content-center ${isDark ? 'text-white-50' : 'text-muted'}`}>
                  <Icon kind={s.kind} />
                </a>
              ))}
            </div>
          </div>

          {/* Right: link columns */}
          <nav className="col-12 col-sm-6 col-lg-3">
            <div className={`mb-2 small text-uppercase fw-semibold ${isDark ? 'text-white' : 'text-dark'}`}>Use Link</div>
            <ul className="list-unstyled m-0">
              {useLinks.map((l, i) => (
                <li key={i} className="mb-2">
                  <a href={l.href} className={`text-decoration-none ${isDark ? 'text-white-50' : 'text-muted'}`}>{l.label}</a>
                </li>
              ))}
            </ul>
          </nav>

          <address className="col-12 col-sm-6 col-lg-3 not-italic">
            <div className={`mb-2 small text-uppercase fw-semibold ${isDark ? 'text-white' : 'text-dark'}`}>Company</div>
            <div className={isDark ? 'text-white-50' : 'text-muted'}>
              {addressLines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </address>
        </div>
      </div>
    </footer>
  );
} 